/**
 * Self-service account deletion (App Store 5.1.1(v) / Google Play). Clients
 * only — coach accounts are managed in admin. Requires typing DELETE, then
 * DELETE /api/me wipes personal data, anonymizes the account and clears the
 * session; we hard-reload to the sign-in screen with a confirmation banner.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useBrand } from '@/lib/useBrand';

export function DeleteAccountCard() {
  const brand = useBrand();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmDelete() {
    setBusy(true);
    setError(null);
    try {
      await api.del('/api/me', { confirm: 'DELETE' });
      window.location.replace('/?account_deleted=1');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'coach_account'
          ? 'Coach accounts are managed by your admin.'
          : 'Something went wrong. Your account was not deleted, please try again.'
      );
      setBusy(false);
    }
  }

  return (
    <div className="card flex flex-col gap-3 mt-4" style={{ borderColor: 'rgba(239,68,68,0.25)' }}>
      <div>
        <p className="font-semibold" style={{ color: 'var(--error)' }}>Delete account</p>
        <p className="text-xs mt-1" style={{ color: 'var(--gray-400)' }}>
          Permanently delete your {brand.name} account and your data: workouts, nutrition, water,
          measurements, health data, goals, messages and booked sessions. Upcoming sessions are
          cancelled. Payment and credit records are kept for accounting with your name and email
          removed. This can't be undone.
        </p>
      </div>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="self-start flex items-center gap-2 text-sm px-4 py-2 rounded-md border"
          style={{ borderColor: 'rgba(239,68,68,0.4)', color: 'var(--error)' }}
        >
          <Trash2 size={15} /> Delete my account
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <label className="text-xs" style={{ color: 'var(--gray-300)' }}>
            Type <strong style={{ color: 'var(--cream)' }}>DELETE</strong> to confirm
          </label>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="input"
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="DELETE"
          />
          {error && <p className="text-xs" style={{ color: 'var(--error)' }}>{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={confirmDelete}
              disabled={typed.trim() !== 'DELETE' || busy}
              className="flex-1 py-2.5 rounded-md text-sm font-semibold disabled:opacity-40"
              style={{ background: 'var(--error)', color: 'white' }}
            >
              {busy ? 'Deleting…' : 'Permanently delete'}
            </button>
            <button
              onClick={() => { setOpen(false); setTyped(''); setError(null); }}
              disabled={busy}
              className="px-4 py-2.5 rounded-md text-sm border"
              style={{ borderColor: 'rgba(255,255,255,0.15)', color: 'var(--gray-300)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
